import styled, { CSSObject } from 'foundations-theming/utils';
import type {
  Action,
  ActionsMenuProps,
  OptionTextColor,
  ReferenceLayout,
} from './ActionsMenu.types';
import { getReferencePadding } from './ActionsMenu.helpers';
import { deepMerge } from '../../transitional/utils/deepMerge';
import { JUPITER_LAYER } from '../../transitional/constants';
import { HEIGHT } from '../../constants/nonTokenisedValues';
import { getPopoverContainerBorderStyles } from '../../transitional/components/Popover/Popover';

export const StyledContainer = styled.div<{
  $styleOverrides: CSSObject;
}>(({ $styleOverrides }) => {
  return deepMerge(
    {
      inlineSize: '100%',
    },
    $styleOverrides
  );
});

export const StyledReferenceWrapper = styled.div<{
  $referenceSize: ActionsMenuProps['referenceSize'];
  $referenceLayout: ReferenceLayout;
  $marginStyles?: CSSObject;
}>(({ theme, $referenceSize, $referenceLayout, $marginStyles }) => {
  const inlineSize =
    $referenceLayout === 'indicator-only' ? 'fit-content' : '100%';
  const blockSize =
    $referenceSize === 'default' ? theme.space[1000] : theme.space[800];

  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    inlineSize,
    blockSize,
    ...$marginStyles,
  };
});

export const StyledReferenceIndicator = styled.div<{
  $referenceSize: ActionsMenuProps['referenceSize'];
}>(({ theme, $referenceSize }) => {
  const { paddingBlock } = getReferencePadding(theme);
  const referenceHeight =
    $referenceSize === 'default' ? theme.space[1000] : theme.space[800];

  return {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    blockSize: `calc(${referenceHeight} - ${paddingBlock} * 2)`,
    borderRadius: theme.borderRadius['full'],
    flexShrink: 0,
  };
});

export const StyledReferenceText = styled.span(({ theme }) => {
  return {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginInlineEnd: theme.space[200],
  };
});

export const StyledDownCaretIconWrapper = styled.span<{
  $iconSize: string;
}>(({ $iconSize }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    opacity: 0.8,
    inlineSize: $iconSize,
    blockSize: $iconSize,
  };
});

export const StyledMenu = styled.div<{
  $isReferenceHidden?: boolean;
}>(({ theme, $isReferenceHidden }) => {
  return {
    visibility: $isReferenceHidden ? 'hidden' : 'visible',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    outline: 0,
    backgroundColor: theme.color['fill-surface-default'],
    borderRadius: theme.borderRadius[200],
    boxShadow: theme.boxShadow[300],
    zIndex: JUPITER_LAYER,
  };
});

export const StyledMenuOptionsWrapper = styled.div(({ theme }) => {
  // apply the same border styles as the Dropdown popover container
  const borderStyles = getPopoverContainerBorderStyles(theme);

  return {
    borderRadius: 'inherit',
    flexGrow: 1,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.color['fill-tertiary-pressed']} transparent`,
    ...borderStyles,
  };
});

export const StyledMenuOption = styled.div<{
  $isActive: boolean;
  $disabled: Action['disabled'];
  $divider: Action['divider'];
  $isSubheading: Action['isSubheading'];
}>(({ theme, $isActive, $disabled, $divider, $isSubheading }) => {
  return {
    paddingBlock: theme.space[300],
    paddingInline: theme.space[400],
    cursor: 'pointer',
    outline: 0,

    ...($isSubheading && {
      padding: theme.space[200],
    }),

    ...($isActive && {
      backgroundColor: theme.color['fill-field-hover'],
    }),

    ...($disabled && {
      cursor: 'not-allowed',
    }),

    ...(!$disabled && {
      '&:active': {
        backgroundColor: theme.color['fill-tertiary-hover'],
      },
    }),

    ...($divider && {
      borderBlockEndStyle: 'solid',
      borderBlockEndWidth: theme.borderWidth[100],
      borderBlockEndColor: theme.color['border-core-subtle'],
    }),
  };
});

export const StyledMenuOptionContentWrapper = styled.button<{
  $optionTextColor: OptionTextColor;
  $hasIndicator: boolean;
}>(({ theme, $optionTextColor, $hasIndicator }) => {
  return {
    background: 'none',
    font: 'inherit',
    cursor: 'inherit',
    outline: 'inherit',
    position: 'relative',
    border: 'none',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    display: 'flex',
    gap: $hasIndicator ? theme.space[200] : theme.space[0],
    color: theme.color[$optionTextColor],
  };
});

export const StyledMenuOptionIndicator = styled.span(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    height: HEIGHT.ACTIONS_MENU_MENU_OPTION,
    borderRadius: theme.borderRadius['full'],
  };
});
